import {Box, TextField, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from "react";
import {useQuery} from "react-query";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {FOLIO_FIELD_GAP} from "../editor/folioStyles";

interface FiasProviderProps {
    onDataChange: (isValid: boolean, data: any) => void;
    profileId?: string;
    /** The folio dialog's payment block: a small field, no title repeating the chosen payment method. */
    compact?: boolean;
}

/**
 * Room display for a FIAS room charge.
 *
 * FIAS routes a charge purely by room number, so the room must be correct — and staff must NOT have to
 * judge which room is right. The system therefore resolves the guest's single CURRENT room server-side
 * (deterministically, live from reservations) and we simply show it, read-only. We never present a list
 * of rooms to choose from, not even for a multi-room group booking. When the guest has no current room
 * on record we fall back to manual entry. The backend re-resolves and overrides the room before posting,
 * so the displayed room and the posted room always agree. See features/fias-room-charge-routing-safety.md.
 */
const FiasProvider = ({profileId, onDataChange, compact}: FiasProviderProps) => {
    const {t} = useTranslation();
    const fieldSize = compact ? 'small' : undefined;
    const [roomId, setRoomId] = useState('');

    const activeStayQuery = useQuery(
        ['activeStayRoom', profileId],
        () => ProfileService.getActiveStay(profileId!),
        {enabled: !!profileId, refetchOnWindowFocus: false}
    );

    const resolvedRoom = activeStayQuery.data?.roomId ?? '';
    const hasResolvedRoom = !!resolvedRoom;

    // The system chose the room — adopt it verbatim. (Manual entry only when nothing was resolved.)
    useEffect(() => {
        if (resolvedRoom) setRoomId(resolvedRoom);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStayQuery.data]);

    // Validate and pass data up on every change.
    useEffect(() => {
        onDataChange(!!roomId, {roomId, provider: 'fias'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId]);

    return (
        <Box sx={{gap: compact ? FOLIO_FIELD_GAP : '12px', width: '100%', display: 'flex', flexDirection: 'column'}}>
            {!compact && <Typography>{t('FIAS Provider Configuration')}</Typography>}

            {hasResolvedRoom ? (
                <TextField
                    fullWidth
                    size={fieldSize}
                    label={t('Room')}
                    value={roomId}
                    InputProps={{readOnly: true}}
                    helperText={t("Automatically set to the guest's current room")}
                />
            ) : (
                <TextField
                    fullWidth
                    size={fieldSize}
                    label={t('Room ID')}
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                    required
                    helperText={t('No current room found for this guest — enter it manually')}
                />
            )}
        </Box>
    );
};

export default FiasProvider;
